/**
 * Module-level persistent store for screen share chat messages.
 * Lives outside React's component tree so messages survive
 * ScreenShareModal mount/unmount cycles.
 */

export interface ScreenShareChatMessage {
  author: string
  authorId?: string
  /** Colyseus session id; matches LiveKit identity */
  authorSessionId?: string
  content: string
  createdAt: number
  roomId?: string
}

type Listener = (messages: ScreenShareChatMessage[]) => void

const MAX_MESSAGES = 200

class ScreenShareChatStore {
  private messagesBySession: Map<string, ScreenShareChatMessage[]> = new Map()
  private listeners: Set<Listener> = new Set()
  private activeSessionId: string = ''

  getActiveSessionId(): string {
    return this.activeSessionId
  }

  setActiveSession(sessionId: string): void {
    this.activeSessionId = sessionId
  }

  getMessages(sessionId: string): ScreenShareChatMessage[] {
    return this.messagesBySession.get(sessionId) || []
  }

  getActiveMessages(): ScreenShareChatMessage[] {
    if (!this.activeSessionId) return []
    return this.getMessages(this.activeSessionId)
  }

  /**
   * Bulk-set messages for a session (used for initial sync from Colyseus state).
   * Deduplicates against existing messages by createdAt+content.
   */
  setMessages(sessionId: string, messages: ScreenShareChatMessage[]): void {
    this.messagesBySession.set(sessionId, messages.slice(-MAX_MESSAGES))
    if (sessionId === this.activeSessionId) {
      this.notifyListeners()
    }
  }

  addMessage(sessionId: string, message: ScreenShareChatMessage): boolean {
    const existing = this.messagesBySession.get(sessionId) || []

    const isDuplicate = existing.some(
      (m) =>
        m.createdAt === message.createdAt &&
        m.content === message.content &&
        m.author === message.author
    )
    if (isDuplicate) return false

    const updated = [...existing, message].slice(-MAX_MESSAGES)
    this.messagesBySession.set(sessionId, updated)

    if (sessionId === this.activeSessionId) {
      this.notifyListeners()
    }
    return true
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private notifyListeners(): void {
    const messages = this.getActiveMessages()
    this.listeners.forEach((fn) => fn(messages))
  }

  clearSession(sessionId: string): void {
    this.messagesBySession.delete(sessionId)
    if (sessionId === this.activeSessionId) {
      this.notifyListeners()
    }
  }

  clearAll(): void {
    this.messagesBySession.clear()
    this.activeSessionId = ''
    this.notifyListeners()
  }
}

export const screenshareChatStore = new ScreenShareChatStore()
